import { ApiError } from '../api/client';

export interface ErrorInfo {
  title: string;
  message: string;
  hint?: string;
  /** Individual problems (blockers, collisions, missing values). */
  items: string[];
  /** What the failure means for the system state (NFR-UX-01: "what was changed"). */
  changed?: string;
  jobId?: string;
  code?: string;
  status?: number;
}

const TITLES: Record<string, string> = {
  unauthenticated: 'You are not signed in',
  session_expired: 'Your session expired',
  forbidden: 'Not permitted',
  not_found: 'Not found',
  validation_failed: 'The request is not valid',
  conflict: 'Conflict with the current state',
  prerequisite_missing: 'A prerequisite is missing',
  branch_rule_violation: 'This branch may not be deployed here',
  config_incomplete: 'Configuration is incomplete',
  gitlab_unavailable: 'GitLab is unavailable',
  gitlab_error: 'GitLab returned an error',
  agent_unavailable: 'The VPS agent is unavailable',
  resource_collision: 'Resource collision',
  confirmation_required: 'Confirmation required',
  unsupported: 'Not supported',
  internal_error: 'Internal server error',
  network_error: 'The server could not be reached',
  server_unreachable: 'The server could not be reached',
};

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String) : [];
}

export function describeError(err: unknown): ErrorInfo {
  if (err instanceof ApiError) {
    const d = err.details && typeof err.details === 'object' && !Array.isArray(err.details) ? (err.details as Record<string, unknown>) : {};
    const items = [...err.blockers, ...stringList(d.collisions), ...stringList(d.missing).map((m) => `Missing: ${m}`), ...stringList(d.missingFiles).map((m) => `Missing file: ${m}`)];
    let changed: string | undefined;
    if (err.status === 0) changed = 'The request may not have reached the server. Check the current state before retrying.';
    else if (err.status >= 500) changed = 'The outcome is uncertain. Check the jobs list and the audit log before retrying.';
    else if (err.status >= 400 && err.status !== 401) changed = 'The request was rejected, so nothing was changed.';
    return {
      title: TITLES[err.code] ?? 'Request failed',
      message: err.message,
      hint: err.hint,
      items,
      changed,
      jobId: err.jobId,
      code: err.code,
      status: err.status,
    };
  }
  if (err instanceof Error) return { title: 'Something went wrong', message: err.message, items: [] };
  return { title: 'Something went wrong', message: String(err), items: [] };
}

export function isApiError(err: unknown, code?: string): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}
